import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AssignmentPage } from '../assignment.page';
import { BasePage } from '../../base.page';
import { ChartData } from '../../../ui/charts/doughnut/doughnut-chart.component';
import {
    ContractDataInterface,
    ContractInterface,
    EvaluateFeeUseCase,
    FeeInterface,
    GetContractDataUseCase,
    GetContractUseCase,
    GetModelPdfUseCase,
    GetValidModelsUseCase,
    IdInterface,
    Transaction,
    TypeDistributionInterface,
    ValidModelInterface,
} from '../../../../core';
import { DownloadHelper } from '../../../utils/download-helper';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { Fee } from '../../../domain/data';
import { ParentEvents } from '../../../utils/parent-events.service';
import { MainRoutes } from '../../../main-routes.enum';
import { Serializer } from '../../../client/helpers/serializer/serializer';

@Component({
    templateUrl: './assignment-operate.page.html',
    styleUrls: ['./assignment-operate.page.scss'],
})
export class AssignmentOperatePage extends BasePage implements AfterViewInit, OnInit {
    public chartData: ChartData[] = [];
    public contract: ContractInterface;
    public currentModelId: IdInterface;
    public currentModelName: string;
    public fee: FeeInterface;
    public feedAmount = 0;
    public validModels: ValidModelInterface[] = [];
    public currentSelected: boolean = false;
    
    constructor(
        assignmentPage: AssignmentPage,
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private evaluateFeeUseCase: EvaluateFeeUseCase,
        private getContractDataUseCase: GetContractDataUseCase,
        private getContractUseCase: GetContractUseCase,
        private getModelPDFUseCase: GetModelPdfUseCase,
        private getValidModelsUseCase: GetValidModelsUseCase,
        private router: Router,
        private serializer: Serializer
    ) {
        super(parentEvents);
        assignmentPage.hideBackButton();
    }

    ngOnInit(): void {
        this.getContractAndModels();
        this.getValidModels();
    }

    private getContractAndModels(): void {
        this.getContractUseCase
            .bind()
            .pipe(
                switchMap((transaction) =>
                    transaction.map(
                        (contract) => {
                            this.contract = contract;
                            this.fee = new Fee(contract.transfer.total);
                            this.feedAmount = contract.feedAmount;
                            return this.getContractDataUseCase.bind();
                        },
                        (throwable) => of(Transaction.error(throwable.message))
                    )
                )
            )
            .subscribe((transaction) => {
                transaction.fold(
                    (data) => {
                        this.currentModelId = data.modelId;
                        this.currentModelName = data.model.name;
                        this.fee = new Fee(this.contract.transfer.total, this.currentModelId);
                    },
                    (throwable) =>
                        this.dialog.open(ErrorDialog, {
                            data: { text: throwable.message },
                        })
                );
            });
    }

    private getValidModels() {
        this.getValidModelsUseCase.bind().subscribe((transaction) => {
            transaction.fold(
                (models) => {
                    this.validModels = models;
                    if (!this.validModels.length) {
                        this.dialog.open(ErrorDialog, {
                            data: { text: 'error.not-models-found' },
                        });
                    }
                },
                (throwable) =>
                    this.dialog.open(ErrorDialog, {
                        data: { text: throwable.message },
                    })
            );
        });
    }

    onChangeDistributions(data: TypeDistributionInterface[]) {
        setTimeout(() => {
            this.chartData = data.map((type) => ({
                name: type.name,
                value: type.value,
                weight: type.weight,
            }));
        }, 1);
    }

    onDownload(modelId: IdInterface) {
        this.getModelPDFUseCase.bind({ id: modelId }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                DownloadHelper.downloadBase64(transaction.data.content, transaction.data.name);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onEvaluateFee(fee: FeeInterface) {
        this.fee = fee;
        this.fee.evaluationType = 'changemodelportfolio';
        this.evaluateFeeUseCase.bind({ data: fee }).subscribe((transactionFee) => {
            transactionFee.fold(
                (feedAmount) => (this.feedAmount = feedAmount),
                (throwable) =>
                    this.dialog.open(ErrorDialog, {
                        data: { text: throwable.message },
                    })
            );
        });
    }

    onCurrentSelected(currectSelected: boolean) {
        this.currentSelected = currectSelected;
    }

    onSubmit() {
        this.router.navigateByUrl(MainRoutes.ASSIGNMENT_SIGN, {
            state: { fee: this.serializer.toJson(this.fee) },
        });
    }
}
